import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { Close as IconClose, Print as IconPrint } from "@mui/icons-material";
import moment from "moment";

import comprobanteService from "../services/comprobanteService";
import { PaperComponentDraggable } from "./common/PaperComponentDraggable";

// La serie y el numero se muestran juntos como B001-000014, que es como se
// identifican los comprobantes y sus pagos en las pantallas.
const serieNumero = (comprobante) => {
    if (!comprobante) return "";

    return comprobante.serie + "-" + String(comprobante.numero).padStart(6, "0");
};

const monto = (valor) => (parseFloat(valor) || 0).toFixed(2);

// Muestra los datos de un comprobante en una ventana: la cabecera, el detalle de
// sus items y el detalle de sus pagos. Es solo lectura: sirve para consultarlo
// desde los listados sin salir de la pagina.
const ComprobanteDialogo = ({ idComprobante, open, onClose }) => {
    const [cargando, setCargando] = useState(false);
    const [comprobante, setComprobante] = useState(null);
    const [detalle, setDetalle] = useState([]);
    const [pagos, setPagos] = useState([]);
    const [error, setError] = useState("");

    const cargar = useCallback(async (id) => {
        if (!id || id <= 0) return;

        setCargando(true);
        setError("");

        try {
            // Cabecera, detalle y pagos del comprobante. Los pagos se piden con
            // los anulados para poder ver el estado real de cada pago.
            const respuestaComprobante = await comprobanteService.getComprobanteView(id);
            const respuestaDetalle = await comprobanteService.getsComprobanteDetalleView(id);
            const respuestaPagos = await comprobanteService.getsComprobantePagoView(id, true);

            const cabecera = (respuestaComprobante && respuestaComprobante.data)
                ? respuestaComprobante.data.data
                : null;

            setComprobante(cabecera);
            setDetalle((respuestaDetalle && respuestaDetalle.data && respuestaDetalle.data.data) || []);
            setPagos((respuestaPagos && respuestaPagos.data && respuestaPagos.data.data) || []);

            if (!cabecera) setError("No se encontro el comprobante.");
        }
        catch (err) {
            console.log("Err: ", err);
            setComprobante(null);
            setDetalle([]);
            setPagos([]);
            setError("No se pudieron cargar los datos del comprobante.");
        }
        finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            cargar(idComprobante);
        }
        else {
            // Al cerrar se limpia todo para que la ventana abra en blanco.
            setComprobante(null);
            setDetalle([]);
            setPagos([]);
            setError("");
        }
    }, [open, idComprobante, cargar]);

    const imprimir = () => {
        window.open("/receipt/print/" + idComprobante, "_blank");
    };

    // Cada dato de la cabecera se muestra con su etiqueta encima del valor, para
    // que la etiqueta y el valor nunca se superpongan.
    const dato = (etiqueta, valor, xs = 6, sm = 3) => (
        <Grid item xs={xs} sm={sm}>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", lineHeight: "1.5" }}>
                {etiqueta}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.5" }}>{valor}</Typography>
        </Grid>
    );

    return (
        <Dialog open={open} fullWidth maxWidth="md" onClose={onClose} PaperComponent={PaperComponentDraggable}>
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={10} sm={10}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {comprobante ? "Comprobante " + serieNumero(comprobante) : "Comprobante"}
                        </Typography>
                    </Grid>
                    <Grid item xs={2} sm={2} display="flex" justifyContent="flex-end">
                        <Tooltip title={"Imprimir"}>
                            <span>
                                <IconButton aria-label="imprimir comprobante" size="small"
                                    disabled={!idComprobante || idComprobante <= 0}
                                    onClick={imprimir}>
                                    <IconPrint fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton aria-label="cerrar" size="small" onClick={onClose}>
                            <IconClose fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                {cargando && (
                    <Box display="flex" justifyContent="center" alignItems="center" padding={4}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && !cargando && (
                    <Alert severity="warning" sx={{ marginTop: "12px" }}>{error}</Alert>
                )}

                {!cargando && !error && comprobante && (
                    <Box>
                        {/* Cabecera del comprobante. */}
                        <Grid container spacing={1}>
                            {dato("Serie-Numero", serieNumero(comprobante))}
                            {dato("Fecha", moment(comprobante.fecha).format("DD/MM/YYYY HH:mm"))}
                            {dato("Tipo", comprobante.tipo)}
                            {dato("Estado", comprobante.estado)}
                            {dato("Estado del Pago", comprobante.estadoPago)}
                            {dato("Cobrador", comprobante.cobrador)}
                            {dato("Pagador", comprobante.pagador)}
                            {dato("Moneda", comprobante.moneda)}
                            {dato("Tipo de Cambio", monto(comprobante.tipoCambio))}
                        </Grid>

                        {comprobante.observaciones && (
                            <Box sx={{ marginTop: "8px" }}>
                                {dato("Observaciones", comprobante.observaciones, 12, 12)}
                            </Box>
                        )}

                        {/* Detalle del comprobante. */}
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginTop: "16px" }}>
                            {"Detalle"}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                                    <TableCell sx={{ color: "white" }}>{"Item"}</TableCell>
                                    <TableCell sx={{ color: "white" }} align="center">{"Cantidad"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{"Concepto de Cobro"}</TableCell>
                                    <TableCell sx={{ color: "white" }} align="right">{"Unitario"}</TableCell>
                                    <TableCell sx={{ color: "white" }} align="right">{"Sub Total"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detalle.map((row) => (
                                    <TableRow key={row.numero}>
                                        <TableCell>{String(row.numero).padStart(3, "0")}</TableCell>
                                        <TableCell align="center">{row.cantidad}</TableCell>
                                        <TableCell>{row.conceptoCobro}</TableCell>
                                        <TableCell align="right">{monto(row.precioUnitario)}</TableCell>
                                        <TableCell align="right">{monto(row.subTotal)}</TableCell>
                                    </TableRow>
                                ))}
                                {detalle.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">{"Sin detalle"}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Detalle de pagos. */}
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginTop: "16px" }}>
                            {"Pagos"}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                                    <TableCell sx={{ color: "white" }}>{"Pago"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{"Medio"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{"Entidad"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{"Fecha"}</TableCell>
                                    <TableCell sx={{ color: "white" }} align="right">{"Monto"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{"Estado"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagos.map((row) => (
                                    <TableRow key={row.numero}>
                                        <TableCell>{String(row.numero).padStart(3, "0")}</TableCell>
                                        <TableCell>{row.medioPago}</TableCell>
                                        <TableCell>{row.entidadPago}</TableCell>
                                        <TableCell>{moment(row.fechaPago).format("DD/MM/YYYY")}</TableCell>
                                        <TableCell align="right">{monto(row.monto)}</TableCell>
                                        <TableCell>{row.estado}</TableCell>
                                    </TableRow>
                                ))}
                                {pagos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">{"Sin pagos"}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Totales. */}
                        <Grid container spacing={1} sx={{ marginTop: "12px" }}>
                            {dato("Sub Total", monto(detalle.reduce((suma, row) => suma + (parseFloat(row.subTotal) || 0), 0)))}
                            {dato("Total", monto(comprobante.montoTotal))}
                            {dato("Saldo", monto(comprobante.montoSaldo))}
                            {comprobante.idEstadoPago == 145 && dato("Saldo Disponible", monto(comprobante.saldoDisponible))}
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{"Cerrar"}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ComprobanteDialogo;
